import React, { useState } from 'react';
import axios from 'axios';
import Spinner from './Spinner';

const KidFriendlyImageCreation = ({ chat, formToken, user, standalone = false }) => {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('cartoon');
  const [difficulty, setDifficulty] = useState('beginner');
  const [layout, setLayout] = useState('1:1');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [avatarSet, setAvatarSet] = useState(false);
  const [backgroundSet, setBackgroundSet] = useState(false);
  const [mobileBackgroundSet, setMobileBackgroundSet] = useState(false);
  const [chatImageId, setChatImageId] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [enhancePrompt, setEnhancePrompt] = useState(true);
  const [showPromptTips, setShowPromptTips] = useState(false);

  const isColoringPage = chat.chatType === 'coloring_page';
  const isAvatar = chat.chatType === 'avatar_creation';

  const promptTips = [
    "🎨 Use descriptive words like 'fluffy', 'sparkly', or 'colorful'",
    "🌟 Describe what your character is doing - playing, dancing, flying!",
    "🌈 Add details about the setting - forest, castle, underwater world",
    "✨ Include emotions - happy, excited, peaceful, adventurous",
    "🦄 Think about magical elements - rainbows, stars, glowing effects"
  ];

  const styleExamples = {
    cartoon: "Perfect for fun characters and bright colors! 🎪",
    watercolor: "Soft and dreamy like painting with water! 🎨",
    realistic: "Looks like a photograph! 📸",
    pencil_sketch: "Like drawing with your favorite pencil! ✏️",
    oil_painting: "Rich and textured like museum art! 🖼️"
  };

  const createImage = async () => {
    setLoading(true);

    try {
      const options = isColoringPage
        ? { difficulty }
        : { style, layout: isAvatar ? '1:1' : layout };

      const response = await axios.post(
        `/api/v1/chats/${chat.id}/create_image_from_prompt`,
        {
          message: prompt,
          options,
          enhance_prompt: enhancePrompt
        },
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
          },
        }
      );

      setGeneratedImage(response.data.imageUrl);
      setChatImageId(response.data.id);
      setIsPublic(response.data.public);
    } catch (error) {
      console.error("Error creating image:", error);
    } finally {
      setLoading(false);
    }
  };

  const setAsAvatar = async () => {
    try {
      const payload = { user: { avatar_id: chatImageId } };
      await axios.patch(
        `/api/v1/users/${user.id}.json`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setAvatarSet(true);
    } catch (error) {
      console.error("Error setting avatar:", error);
    }
  };

  const setAsBackground = async () => {
    try {
      const payload = { user: { background_id: chatImageId } };
      await axios.patch(
        `/api/v1/users/${user.id}.json`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setBackgroundSet(true);
    } catch (error) {
      console.error("Error setting background:", error);
    }
  };

  const setAsMobileBackground = async () => {
    try {
      const payload = { user: { mobile_background_id: chatImageId } };
      await axios.patch(
        `/api/v1/users/${user.id}.json`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setMobileBackgroundSet(true);
    } catch (error) {
      console.error("Error setting mobile background:", error);
    }
  };

  const toggleShareOnTYB = async () => {
    try {
      const response = await axios.patch(
        `/api/v1/chat_images/${chatImageId}.json`,
        { chat_image: { public: !isPublic } },
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json'
          },
        }
      );
      setIsPublic(response.data.chat_image.public);
    } catch (error) {
      console.error("Error toggling image visibility:", error);
    }
  };

  const downloadImage = () => {
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = 'tyb_generated_image.png';
    link.click();
  };

  const getPageTitle = () => {
    if (isColoringPage) return '🖍️ Create Your Coloring Page';
    if (isAvatar) return '🎭 Create Your Avatar';
    return '🎨 Create Your Amazing Image';
  };

  const getPromptPlaceholder = () => {
    if (isColoringPage) return "A happy butterfly in a garden...";
    if (isAvatar) return "A friendly character with big eyes...";
    return "A magical unicorn flying through rainbow clouds...";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
            {getPageTitle()}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Let your imagination run wild! Describe what you want to see and watch the magic happen ✨
          </p>
        </div>

        {/* Generated Image Display - Full Width */}
        {generatedImage && (
          <div className="bg-white rounded-3xl shadow-xl p-8 border-4 border-gradient-to-r from-blue-200 to-green-200 mb-8">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-gray-800 mb-4 flex items-center justify-center">
                <span className="mr-2">🎉</span>
                Your Amazing Creation!
                <span className="ml-2">🌟</span>
              </h3>
              <p className="text-gray-600 mb-6">Look what you created! Isn't it wonderful?</p>
            </div>
            
            <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Image */}
              <div className="relative">
                <img
                  src={generatedImage}
                  alt="Your amazing creation"
                  className="w-full rounded-2xl shadow-lg border-4 border-gray-100"
                />
                <div className="absolute -top-2 -right-2 bg-yellow-400 text-yellow-800 px-3 py-1 rounded-full text-sm font-bold transform rotate-12">
                  Amazing! ⭐
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-4">
                <div className="bg-purple-50 rounded-2xl p-6">
                  <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">🚀</span>
                    What would you like to do?
                  </h4>
                  
                  <div className="space-y-3">
                    <button
                      onClick={downloadImage}
                      className="w-full bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      <span className="mr-2">⬇️</span>
                      Download My Image
                    </button>
                    
                    {/* Show appropriate background button based on layout */}
                    {!isAvatar && layout === '16:9' && (
                      <button
                        onClick={setAsBackground}
                        className={`w-full font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg ${
                          backgroundSet
                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
                        }`}
                        disabled={backgroundSet}
                      >
                        <span className="mr-2">🖥️</span>
                        {backgroundSet ? 'Set as Desktop Background! ✅' : 'Set as My Desktop Background'}
                      </button>
                    )}

                    {!isAvatar && layout === '9:16' && (
                      <button
                        onClick={setAsMobileBackground}
                        className={`w-full font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg ${
                          mobileBackgroundSet
                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white'
                        }`}
                        disabled={mobileBackgroundSet}
                      >
                        <span className="mr-2">📱</span>
                        {mobileBackgroundSet ? 'Set as Mobile Background! ✅' : 'Set as My Mobile Background'}
                      </button>
                    )}
                    
                    {isAvatar && (
                      <button
                        onClick={setAsAvatar}
                        className={`w-full font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg ${
                          avatarSet 
                            ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                            : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white'
                        }`}
                        disabled={avatarSet}
                      >
                        <span className="mr-2">👤</span>
                        {avatarSet ? 'Set as Avatar! ✅' : 'Set as My Avatar'}
                      </button>
                    )}
                    
                    <button
                      onClick={toggleShareOnTYB}
                      className={`w-full font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg ${
                        isPublic 
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600' 
                          : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                      } text-white`}
                    >
                      <span className="mr-2">{isPublic ? '👁️' : '🌍'}</span>
                      {isPublic ? 'Hide My Image' : 'Share with Everyone!'}
                    </button>
                    
                    <button
                      onClick={() => {
                        setGeneratedImage(null);
                        setChatImageId(null);
                        setPrompt('');
                        setAvatarSet(false);
                        setBackgroundSet(false);
                        setMobileBackgroundSet(false);
                      }}
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg"
                    >
                      <span className="mr-2">✨</span>
                      Create Another Image
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Creation Form - Only show when no image or loading */}
        {!generatedImage && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column: Creation Panel */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border-4 border-gradient-to-r from-purple-200 to-pink-200">
              {loading ? (
                <div className="flex flex-col justify-center items-center h-full min-h-[400px]">
                  <Spinner />
                  <p className="mt-4 text-xl text-purple-600 font-semibold animate-pulse">
                    Creating your amazing image... ✨
                  </p>
                  <div className="mt-2 flex space-x-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              ) : (
              <div className="space-y-6">
                {/* Prompt Input */}
                <div className="space-y-3">
                  <label className="flex items-center text-lg font-bold text-gray-800">
                    <span className="text-2xl mr-2">💭</span>
                    Tell me what you want to create!
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={getPromptPlaceholder()}
                    className="w-full p-4 border-3 border-purple-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 text-lg resize-none transition-all duration-200"
                    rows="4"
                  />
                  
                  {/* Prompt Tips Toggle */}
                  <button
                    onClick={() => setShowPromptTips(!showPromptTips)}
                    className="text-sm text-purple-600 hover:text-purple-800 flex items-center font-medium transition-colors"
                  >
                    <span className="mr-1">💡</span>
                    {showPromptTips ? 'Hide' : 'Show'} prompting tips
                  </button>
                  
                  {/* Prompt Tips */}
                  {showPromptTips && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-4">
                      <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                        <span className="text-xl mr-2">🌟</span>
                        Tips for awesome images:
                      </h4>
                      <ul className="space-y-2">
                        {promptTips.map((tip, index) => (
                          <li key={index} className="text-sm text-gray-700 flex items-start">
                            <span className="mr-2 flex-shrink-0">{tip.split(' ')[0]}</span>
                            <span>{tip.substring(tip.indexOf(' ') + 1)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Enhancement Toggle */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="enhancePrompt"
                      checked={enhancePrompt}
                      onChange={(e) => setEnhancePrompt(e.target.checked)}
                      className="w-5 h-5 text-blue-600 border-2 border-blue-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="enhancePrompt" className="flex items-center text-gray-800 font-medium">
                      <span className="text-xl mr-2">🎨</span>
                      Help me make my prompt even better!
                    </label>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 ml-8">
                    {enhancePrompt 
                      ? "We'll add extra details to make your image amazing! ✨" 
                      : "We'll use your prompt exactly as you wrote it! 📝"
                    }
                  </p>
                </div>

                {/* Coloring Page Difficulty */}
                {isColoringPage && (
                  <div className="space-y-3">
                    <label className="flex items-center text-lg font-bold text-gray-800">
                      <span className="text-2xl mr-2">🎯</span>
                      How challenging should it be?
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {['beginner', 'intermediate', 'expert'].map((level) => (
                        <button
                          key={level}
                          onClick={() => setDifficulty(level)}
                          className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                            difficulty === level
                              ? 'bg-green-200 border-green-400 text-green-800'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="text-center">
                            <div className="text-2xl mb-1">
                              {level === 'beginner' && '🟢'}
                              {level === 'intermediate' && '🟡'}
                              {level === 'expert' && '🔴'}
                            </div>
                            <div className="capitalize font-medium">{level}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Layout Selection */}
                {!isAvatar && (
                  <div className="space-y-3">
                    <label className="flex items-center text-lg font-bold text-gray-800">
                      <span className="text-2xl mr-2">📐</span>
                      Choose your layout!
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        {
                          value: '1:1',
                          label: 'Square',
                          emoji: '⬜',
                          description: 'Perfect for your avatar and profile picture!'
                        },
                        {
                          value: '9:16',
                          label: 'Tall',
                          emoji: '📱',
                          description: 'Great for your mobile phone background and the TYB app!'
                        },
                        {
                          value: '16:9',
                          label: 'Wide',
                          emoji: '🖥️',
                          description: 'Best for your desktop and tablet background!'
                        }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setLayout(option.value)}
                          className={`p-4 rounded-xl border-2 transition-all duration-200 text-left hover:scale-102 ${
                            layout === option.value
                              ? 'bg-blue-200 border-blue-400 text-blue-800 shadow-lg'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center">
                            <div className="text-3xl mr-4">{option.emoji}</div>
                            <div className="flex-1">
                              <div className="font-bold text-lg">{option.label}</div>
                              <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                            </div>
                            {layout === option.value && (
                              <div className="text-blue-600 text-xl">✅</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Style Selection */}
                <div className="space-y-3">
                  <label className="flex items-center text-lg font-bold text-gray-800">
                    <span className="text-2xl mr-2">🎨</span>
                    Pick your art style!
                  </label>
                  <select
                    value={style}
                    onChange={(e) => setStyle(e.target.value)}
                    className="w-full p-4 border-3 border-purple-200 rounded-xl focus:border-purple-400 text-lg bg-white"
                  >
                    <option value="cartoon">🎪 Cartoon - Fun & Colorful</option>
                    <option value="watercolor">🎨 Watercolor - Soft & Dreamy</option>
                    <option value="realistic">📸 Realistic - Like a Photo</option>
                    <option value="pencil_sketch">✏️ Pencil Sketch - Hand Drawn</option>
                    <option value="oil_painting">🖼️ Oil Painting - Rich & Textured</option>
                    <option value="papercraft">📄 Papercraft - Cut & Folded</option>
                    <option value="stencil">🏷️ Stencil - Bold & Simple</option>
                    <option value="collage">🧩 Collage - Mixed Materials</option>
                  </select>
                  {styleExamples[style] && (
                    <p className="text-sm text-purple-600 bg-purple-50 p-2 rounded-lg">
                      {styleExamples[style]}
                    </p>
                  )}
                </div>

                {/* Create Button */}
                <button
                  onClick={createImage}
                  disabled={!prompt.trim()}
                  className={`w-full py-4 text-xl font-bold rounded-xl transition-all duration-300 transform ${
                    prompt.trim()
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl hover:scale-105'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span className="mr-2">✨</span>
                  Create My Amazing Image!
                  <span className="ml-2">🎨</span>
                </button>
              </div>
              )}
            </div>

            {/* Right Column: Placeholder */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border-4 border-gradient-to-r from-blue-200 to-green-200">
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
                <div className="text-6xl mb-4">🎨</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Ready to Create?</h3>
                <p className="text-gray-600 max-w-sm">
                  Fill out the form on the left and watch the magic happen!
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KidFriendlyImageCreation;